import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import InstituteProfileGate from "@/components/InstituteProfileGate";
import FeedbackWidget from "@/components/FeedbackWidget";

// Keep public-critical pages in main bundle so the landing page is instant.
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Demo from "@/pages/Demo";

// Lazy-load every authenticated/secondary route. Drops landing-page first-load bundle by 60-70%.
const InstituteHome = lazy(() => import("@/pages/InstituteHome"));
const InstituteUpload = lazy(() => import("@/pages/InstituteUpload"));
const InstituteLibrary = lazy(() => import("@/pages/InstituteLibrary"));
const InstituteStudents = lazy(() => import("@/pages/InstituteStudents"));
const InstituteClasses = lazy(() => import("@/pages/InstituteClasses"));
const InstituteGenerate = lazy(() => import("@/pages/InstituteGenerate"));
const InstituteBroadcasts = lazy(() => import("@/pages/InstituteBroadcasts"));
const InstituteAnalytics = lazy(() => import("@/pages/InstituteAnalytics"));
const InstituteTelegram = lazy(() => import("@/pages/InstituteTelegram"));
const InstituteUpgrade = lazy(() => import("@/pages/InstituteUpgrade"));
const StudentHome = lazy(() => import("@/pages/StudentHome"));
const StudentChat = lazy(() => import("@/pages/StudentChat"));
const StudentQuizzes = lazy(() => import("@/pages/StudentQuizzes"));
const StudentNotes = lazy(() => import("@/pages/StudentNotes"));
const SelfStudyHome = lazy(() => import("@/pages/SelfStudyHome"));
const SelfStudyUpgrade = lazy(() => import("@/pages/SelfStudyUpgrade"));
const SelfStudyTelegram = lazy(() => import("@/pages/SelfStudyTelegram"));
const LegalPages = lazy(() => import("@/pages/LegalPages"));
const AdminOverview = lazy(() => import("@/pages/AdminOverview"));
const AdminInstitutes = lazy(() => import("@/pages/AdminInstitutes"));
const AdminUsers = lazy(() => import("@/pages/AdminUsers"));
const AdminBlog = lazy(() => import("@/pages/AdminBlog"));
const AdminAuditLog = lazy(() => import("@/pages/AdminAuditLog"));
const AdminLLMMetrics = lazy(() => import("@/pages/AdminLLMMetrics"));
const AdminHealthDashboard = lazy(() => import("@/pages/AdminHealthDashboard"));
const AdminTelegramDiagnostics = lazy(() => import("@/pages/AdminTelegramDiagnostics"));
const AdminAnalytics = lazy(() => import("@/pages/AdminAnalytics"));
const AdminUploadControls = lazy(() => import("@/pages/AdminUploadControls"));
const AdminPlansConfig = lazy(() => import("@/pages/AdminPlansConfig"));
const AdminRagTest = lazy(() => import("@/pages/AdminRagTest"));
const AdminErasureQueue = lazy(() => import("@/pages/AdminErasureQueue"));
const AdminFeedback = lazy(() => import("@/pages/AdminFeedback"));
const TutorSettings = lazy(() => import("@/pages/TutorSettings"));
const TwoFactorSetup = lazy(() => import("@/pages/TwoFactorSetup"));
const AccountData = lazy(() => import("@/pages/AccountData"));
const Setup = lazy(() => import("@/pages/Setup"));
const EmailVerify = lazy(() => import("@/pages/EmailVerify"));
const BlogPage = lazy(() => import("@/pages/Blog"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const ExamLanding = lazy(() => import("@/pages/ExamLanding"));

// Wrappers for named exports inside lazy-loaded modules
const PrivacyPolicy = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.TermsOfService })));
const RefundPolicy = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.RefundPolicy })));
const CancellationPolicy = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.CancellationPolicy })));
const CookiePolicy = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.CookiePolicy })));
const GrievanceRedressal = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.GrievanceRedressal })));
const ContactPage = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.ContactPage })));
const BlogIndex = lazy(() => import("@/pages/Blog").then(m => ({ default: m.BlogIndex })));
const BlogDetail = lazy(() => import("@/pages/Blog").then(m => ({ default: m.BlogDetail })));

function RouteLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] text-[#7A7369]" data-testid="route-loader">
      <div className="w-8 h-8 border-2 border-[#C24933]/30 border-t-[#C24933] rounded-full animate-spin" />
    </div>
  );
}

function DashboardRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <RouteLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "super_admin") return <Navigate to="/admin" replace />;
  if (user.role === "self_study_student") return <Navigate to="/selfstudy" replace />;
  return <Navigate to={user.role === "institute_admin" ? "/institute" : "/student"} replace />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<RouteLoader />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/exams/:slug" element={<ExamLanding />} />
        <Route path="/demo" element={<Demo />} />
        <Route path="/login" element={<Login />} />
        <Route path="/setup" element={<Setup />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/dashboard" element={<DashboardRedirect />} />

        <Route path="/institute" element={<ProtectedRoute role="institute_admin"><InstituteHome /></ProtectedRoute>} />
        <Route path="/institute/upload" element={<ProtectedRoute role="institute_admin"><InstituteUpload /></ProtectedRoute>} />
        <Route path="/institute/library" element={<ProtectedRoute role="institute_admin"><InstituteLibrary /></ProtectedRoute>} />
        <Route path="/institute/students" element={<ProtectedRoute role="institute_admin"><InstituteStudents /></ProtectedRoute>} />
        <Route path="/institute/classes" element={<ProtectedRoute role="institute_admin"><InstituteClasses /></ProtectedRoute>} />
        <Route path="/institute/generate" element={<ProtectedRoute role="institute_admin"><InstituteGenerate /></ProtectedRoute>} />
        <Route path="/institute/broadcasts" element={<ProtectedRoute role="institute_admin"><InstituteBroadcasts /></ProtectedRoute>} />
        <Route path="/institute/analytics" element={<ProtectedRoute role="institute_admin"><InstituteAnalytics /></ProtectedRoute>} />
        <Route path="/institute/telegram" element={<ProtectedRoute role="institute_admin"><InstituteTelegram /></ProtectedRoute>} />
        <Route path="/institute/upgrade" element={<ProtectedRoute role="institute_admin"><InstituteUpgrade /></ProtectedRoute>} />

        <Route path="/student" element={<ProtectedRoute role="student"><StudentHome /></ProtectedRoute>} />
        <Route path="/student/chat" element={<ProtectedRoute role="student"><StudentChat /></ProtectedRoute>} />
        <Route path="/student/chat/:sessionId" element={<ProtectedRoute role="student"><StudentChat /></ProtectedRoute>} />
        <Route path="/student/quizzes" element={<ProtectedRoute role="student"><StudentQuizzes /></ProtectedRoute>} />
        <Route path="/student/notes" element={<ProtectedRoute role="student"><StudentNotes /></ProtectedRoute>} />

        <Route path="/selfstudy" element={<ProtectedRoute role="self_study_student"><SelfStudyHome /></ProtectedRoute>} />
        <Route path="/selfstudy/upload" element={<ProtectedRoute role="self_study_student"><InstituteUpload /></ProtectedRoute>} />
        <Route path="/selfstudy/library" element={<ProtectedRoute role="self_study_student"><InstituteLibrary /></ProtectedRoute>} />
        <Route path="/selfstudy/chat" element={<ProtectedRoute role="self_study_student"><StudentChat /></ProtectedRoute>} />
        <Route path="/selfstudy/chat/:sessionId" element={<ProtectedRoute role="self_study_student"><StudentChat /></ProtectedRoute>} />
        <Route path="/selfstudy/quizzes" element={<ProtectedRoute role="self_study_student"><StudentQuizzes /></ProtectedRoute>} />
        <Route path="/selfstudy/notes" element={<ProtectedRoute role="self_study_student"><StudentNotes /></ProtectedRoute>} />
        <Route path="/selfstudy/telegram" element={<ProtectedRoute role="self_study_student"><SelfStudyTelegram /></ProtectedRoute>} />
        <Route path="/selfstudy/upgrade" element={<ProtectedRoute role="self_study_student"><SelfStudyUpgrade /></ProtectedRoute>} />

        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/cancellation-policy" element={<CancellationPolicy />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/grievance" element={<GrievanceRedressal />} />
        <Route path="/contact" element={<ContactPage />} />

        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogDetail />} />

        <Route path="/verify-email" element={<ProtectedRoute><EmailVerify /></ProtectedRoute>} />
        <Route path="/account/2fa" element={<ProtectedRoute><TwoFactorSetup /></ProtectedRoute>} />
        <Route path="/account/data" element={<ProtectedRoute><AccountData /></ProtectedRoute>} />
        <Route path="/institute/tutor-settings" element={<ProtectedRoute role="institute_admin"><TutorSettings /></ProtectedRoute>} />

        <Route path="/admin" element={<ProtectedRoute role="super_admin"><AdminOverview /></ProtectedRoute>} />
        <Route path="/admin/institutes" element={<ProtectedRoute role="super_admin"><AdminInstitutes /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute role="super_admin"><AdminUsers /></ProtectedRoute>} />
        <Route path="/admin/blog" element={<ProtectedRoute role="super_admin"><AdminBlog /></ProtectedRoute>} />
        <Route path="/admin/audit-log" element={<ProtectedRoute role="super_admin"><AdminAuditLog /></ProtectedRoute>} />
        <Route path="/admin/llm-metrics" element={<ProtectedRoute role="super_admin"><AdminLLMMetrics /></ProtectedRoute>} />
        <Route path="/admin/health" element={<ProtectedRoute role="super_admin"><AdminHealthDashboard /></ProtectedRoute>} />
        <Route path="/admin/telegram-diagnostics" element={<ProtectedRoute role="super_admin"><AdminTelegramDiagnostics /></ProtectedRoute>} />
        <Route path="/admin/analytics" element={<ProtectedRoute role="super_admin"><AdminAnalytics /></ProtectedRoute>} />
        <Route path="/admin/upload-controls" element={<ProtectedRoute role="super_admin"><AdminUploadControls /></ProtectedRoute>} />
        <Route path="/admin/plans-config" element={<ProtectedRoute role="super_admin"><AdminPlansConfig /></ProtectedRoute>} />
        <Route path="/admin/rag-test" element={<ProtectedRoute role="super_admin"><AdminRagTest /></ProtectedRoute>} />
        <Route path="/admin/erasure-queue" element={<ProtectedRoute role="super_admin"><AdminErasureQueue /></ProtectedRoute>} />
        <Route path="/admin/feedback" element={<ProtectedRoute role="super_admin"><AdminFeedback /></ProtectedRoute>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <InstituteProfileGate />
            <FeedbackWidget />
            <Toaster richColors position="top-right" />
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}
